import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {Map<string, any>} */
  #productCache = new Map();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleUpsellOnOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleUpsellOnOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
    requestAnimationFrame(() => this.#evaluateUpsell());
  };

  #handleUpsellOnOpen = () => {
    this.#evaluateUpsell();
  };

  async #evaluateUpsell() {
    const indexEl = document.getElementById('lora-bundles-index');
    if (!indexEl) return;
    let raw = indexEl.textContent || '';
    let index;
    try {
      index = JSON.parse(raw);
      // Shop-Metafield JSON kann doppelt-encoded sein
      if (typeof index === 'string') index = JSON.parse(index);
    } catch (e) {
      console.warn('[lora-upsell] index parse failed', e);
      return;
    }
    if (!index?.bundles?.length) return;

    let cart;
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      cart = await res.json();
    } catch (e) {
      console.warn('[lora-upsell] cart fetch failed', e);
      return;
    }

    const triggered = new Set();
    for (const item of cart.items || []) {
      const sugg = item.properties?._suggested_bundle_handle;
      if (sugg) triggered.add(sugg);
      const inBundle = item.properties?._bundle_handle;
      if (inBundle) triggered.add(inBundle);
    }
    if (triggered.size === 0) {
      this.#removeUpsell();
      return;
    }

    // Alle Produkt-IDs im Warenkorb, egal wie sie hineingekommen sind (auch
    // ein Geschenk- oder Einzelkauf ohne Bundle-Property). So bietet der Upsell
    // nichts an, das ohnehin schon im Korb liegt (verhindert doppelte Zeilen,
    // z.B. wenn Baumwolle bereits als Gratis-Geschenk drin ist).
    const allInCartProductIds = new Set();
    for (const item of cart.items || []) {
      if (item.product_id) {
        allInCartProductIds.add(`gid://shopify/Product/${item.product_id}`);
      }
    }

    const upsellBundles = [];
    for (const handle of triggered) {
      const bundle = index.bundles.find((b) => b.handle === handle);
      if (!bundle) continue;
      if (bundle.popup_active === false) continue;

      const inCartProductIds = new Set();
      const inCartSlotIds = new Set();
      for (const item of cart.items || []) {
        if (item.properties?._bundle_handle === handle) {
          if (item.product_id) inCartProductIds.add(`gid://shopify/Product/${item.product_id}`);
          const sid = item.properties._bundle_slot_id;
          if (sid) inCartSlotIds.add(sid);
        } else if (item.properties?._suggested_bundle_handle === handle) {
          if (item.product_id) inCartProductIds.add(`gid://shopify/Product/${item.product_id}`);
        }
      }

      const missingSlots = (bundle.slots || []).filter((slot) => {
        if (slot.is_optional) return false;
        if (slot.slot_id && inCartSlotIds.has(slot.slot_id)) return false;
        const slotProductIds = (slot.options || []).map((o) => o.product_id).filter(Boolean);
        if (slot.product_id) slotProductIds.push(slot.product_id);
        return !slotProductIds.some(
          (pid) => inCartProductIds.has(pid) || allInCartProductIds.has(pid)
        );
      });

      if (missingSlots.length > 0) {
        upsellBundles.push({ bundle, missingSlots });
      }
    }

    if (upsellBundles.length === 0) {
      this.#removeUpsell();
      return;
    }
    await this.#renderUpsell(upsellBundles);
  }

  #removeUpsell() {
    const dialog = this.refs?.dialog;
    if (!dialog) return;
    dialog.querySelector('[data-lora-upsell]')?.remove();
  }

  async #fetchProduct(handle) {
    if (!handle) return null;
    if (this.#productCache.has(handle)) return this.#productCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) {
        this.#productCache.set(handle, null);
        return null;
      }
      const data = await res.json();
      this.#productCache.set(handle, data);
      return data;
    } catch {
      this.#productCache.set(handle, null);
      return null;
    }
  }

  async #renderUpsell(upsellBundles) {
    const dialog = this.refs?.dialog;
    if (!dialog) return;

    this.#removeUpsell();

    // Pre-fetch alle benötigten Produkt-Details für die Cards
    const handles = new Set();
    for (const { missingSlots } of upsellBundles) {
      for (const slot of missingSlots) {
        for (const opt of (slot.options || [])) {
          if (opt.product_handle) handles.add(opt.product_handle);
        }
      }
    }
    await Promise.all([...handles].map((h) => this.#fetchProduct(h)));

    const wrap = document.createElement('aside');
    wrap.className = 'lora-bundle-upsell';
    wrap.dataset.loraUpsell = '';

    for (const { bundle, missingSlots } of upsellBundles) {
      const block = document.createElement('div');
      block.className = 'lora-bundle-upsell__block';

      const headline = document.createElement('p');
      headline.className = 'lora-bundle-upsell__headline';
      headline.textContent = bundle.popup_headline || 'Hol dir den ganzen Look';
      block.appendChild(headline);

      for (const slot of missingSlots) {
        const slotCard = this.#buildUpsellSlotCard(bundle, slot);
        if (slotCard) block.appendChild(slotCard);
      }

      wrap.appendChild(block);
    }

    // Platzierung: zwischen Cart-Items und Summary, damit die Checkout-Box
    // unten erhalten bleibt und der Upsell logisch nach den Items sitzt.
    const summary = dialog.querySelector('.cart-drawer__summary');
    const content = dialog.querySelector('.cart-drawer__content');
    if (summary && content && summary.parentElement === content) {
      content.insertBefore(wrap, summary);
    } else if (content) {
      content.appendChild(wrap);
    } else {
      const inner = dialog.querySelector('.cart-drawer__inner') || dialog;
      inner.appendChild(wrap);
    }
  }

  #buildUpsellSlotCard(bundle, slot) {
    const options = (slot.options || []).filter((o) => (o.default_variant_id || o.product_handle) && o.product_title);
    if (options.length === 0) return null;

    const self = this;
    let activeOptionIdx = 0;
    let activeVariantId = options[0].default_variant_id;

    const card = document.createElement('article');
    card.className = 'lora-bundle-upsell__card';

    const mediaWrap = document.createElement('a');
    mediaWrap.className = 'lora-bundle-upsell__media';
    mediaWrap.rel = 'noopener';

    const imgEl = document.createElement('img');
    imgEl.className = 'lora-bundle-upsell__img';
    imgEl.loading = 'lazy';
    imgEl.decoding = 'async';
    mediaWrap.appendChild(imgEl);

    const qtyBadge = document.createElement('span');
    qtyBadge.className = 'lora-bundle-upsell__qty-badge';
    qtyBadge.hidden = true;
    qtyBadge.setAttribute('aria-label', 'Menge im Bundle');
    mediaWrap.appendChild(qtyBadge);

    const meta = document.createElement('div');
    meta.className = 'lora-bundle-upsell__meta';

    const titleLink = document.createElement('a');
    titleLink.className = 'lora-bundle-upsell__title';
    titleLink.rel = 'noopener';
    meta.appendChild(titleLink);

    const sublineEl = document.createElement('p');
    sublineEl.className = 'lora-bundle-upsell__subline';
    sublineEl.hidden = true;
    meta.appendChild(sublineEl);

    const priceLine = document.createElement('p');
    priceLine.className = 'lora-bundle-upsell__price';
    const priceWasEl = document.createElement('s');
    priceWasEl.className = 'lora-bundle-upsell__price-was';
    priceWasEl.hidden = true;
    const priceNowEl = document.createElement('span');
    priceNowEl.className = 'lora-bundle-upsell__price-now';
    const priceAsterisk = document.createElement('sup');
    priceAsterisk.className = 'lora-bundle-upsell__price-asterisk';
    priceAsterisk.setAttribute('aria-hidden', 'true');
    priceAsterisk.textContent = '*';
    priceLine.appendChild(priceWasEl);
    priceLine.appendChild(priceNowEl);
    priceLine.appendChild(priceAsterisk);
    meta.appendChild(priceLine);

    const legalEl = document.createElement('p');
    legalEl.className = 'lora-bundle-upsell__legal';
    const detailLink = document.createElement('a');
    detailLink.className = 'lora-bundle-upsell__detail-link';
    detailLink.rel = 'noopener';
    detailLink.textContent = 'Details';
    legalEl.appendChild(detailLink);
    meta.appendChild(legalEl);

    let optionSelect = null;
    if (options.length > 1) {
      optionSelect = document.createElement('select');
      optionSelect.className = 'lora-bundle-upsell__select';
      options.forEach((opt, i) => {
        const o = document.createElement('option');
        o.value = String(i);
        o.textContent = opt.product_title;
        optionSelect.appendChild(o);
      });
      optionSelect.addEventListener('change', () => applyOption(parseInt(optionSelect.value, 10) || 0));
      meta.insertBefore(optionSelect, sublineEl);
    }

    const variantOptionsEl = document.createElement('div');
    variantOptionsEl.className = 'lora-bundle-upsell__variant-options';
    meta.appendChild(variantOptionsEl);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lora-bundle-upsell__btn button button-primary';
    btn.textContent = 'Dazupacken';
    btn.addEventListener('click', () =>
      this.#addUpsellItem(bundle, slot, options[activeOptionIdx], activeVariantId, btn),
    );
    meta.appendChild(btn);

    card.appendChild(mediaWrap);
    card.appendChild(meta);

    function computeEffective(fullPriceCents, opt) {
      if (opt.price_override_amount != null) {
        return Math.max(0, Math.round(opt.price_override_amount * 100));
      }
      if (opt.price_override_percent != null) {
        return Math.max(0, Math.round((fullPriceCents * (100 - opt.price_override_percent)) / 100));
      }
      if (bundle.discount_type === 'percent' && bundle.discount_value > 0) {
        return Math.max(0, Math.round((fullPriceCents * (100 - bundle.discount_value)) / 100));
      }
      return fullPriceCents;
    }

    function fmt(cents) {
      return `+${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }

    function setMedia(prod, variant, opt) {
      const url =
        variant?.featured_image?.src ||
        prod?.featured_image ||
        opt.product_image_url ||
        '';
      if (url) {
        imgEl.src = url;
        imgEl.alt = variant?.featured_image?.alt || prod?.title || opt.product_title;
      } else {
        imgEl.removeAttribute('src');
        imgEl.alt = opt.product_title;
      }
    }

    function setPrice(variant, opt) {
      const fullUnit = variant?.price ?? 0;
      const qty = Math.max(1, parseInt(opt?.quantity ?? 1, 10) || 1);
      const effUnit = computeEffective(fullUnit, opt);
      const fullTotal = fullUnit * qty;
      const effTotal = effUnit * qty;
      priceNowEl.textContent = fmt(effTotal);
      if (fullTotal > 0 && effTotal < fullTotal) {
        priceWasEl.textContent = fmt(fullTotal);
        priceWasEl.hidden = false;
      } else {
        priceWasEl.hidden = true;
      }
    }

    function renderVariantPicker(prod, opt) {
      variantOptionsEl.innerHTML = '';
      if (
        !prod ||
        prod.has_only_default_variant ||
        !Array.isArray(prod.options) ||
        prod.options.length === 0 ||
        !Array.isArray(prod.variants)
      ) {
        return;
      }
      const groupId = `lora-up-${Math.random().toString(36).slice(2, 8)}`;
      const firstAvail = prod.variants.find((v) => v.available) || prod.variants[0];

      prod.options.forEach((optEntry, optIdx) => {
        // Shopify product.js liefert options als Strings ODER als Objekte mit .name
        const optName = typeof optEntry === 'string' ? optEntry : optEntry?.name ?? '';

        const seen = new Set();
        prod.variants.forEach((v) => {
          const val = v[`option${optIdx + 1}`] ?? v.options?.[optIdx];
          if (val && !seen.has(val)) seen.add(val);
        });

        // Single-Value-Option: nichts zu wählen, keinen Picker rendern
        if (seen.size <= 1) return;

        const fieldset = document.createElement('fieldset');
        fieldset.className = 'lora-bundle-upsell__variant-option';
        const legend = document.createElement('legend');
        legend.className = 'lora-bundle-upsell__variant-legend';
        legend.textContent = optName;
        fieldset.appendChild(legend);

        const chips = document.createElement('div');
        chips.className = 'lora-bundle-upsell__variant-chips';

        const defaultVal = firstAvail?.[`option${optIdx + 1}`] ?? firstAvail?.options?.[optIdx];

        seen.forEach((val) => {
          const label = document.createElement('label');
          label.className = 'lora-bundle-upsell__variant-chip';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `${groupId}-${optIdx}`;
          input.value = val;
          input.dataset.optIdx = String(optIdx);
          if (val === defaultVal) input.checked = true;
          input.addEventListener('change', () => {
            const sels = prod.options.map((_, i) => {
              const r = variantOptionsEl.querySelector(`input[data-opt-idx="${i}"]:checked`);
              return r?.value ?? null;
            });
            const matched = prod.variants.find((v) =>
              sels.every((s, i) => {
                if (s == null) return true;
                const vv = v[`option${i + 1}`] ?? v.options?.[i];
                return vv === s;
              }),
            );
            if (matched) {
              activeVariantId = matched.id;
              setMedia(prod, matched, opt);
              setPrice(matched, opt);
            }
          });
          const span = document.createElement('span');
          span.className = 'lora-bundle-upsell__variant-chip-label';
          span.textContent = val;
          label.appendChild(input);
          label.appendChild(span);
          chips.appendChild(label);
        });

        fieldset.appendChild(chips);
        variantOptionsEl.appendChild(fieldset);
      });
    }

    function applyOption(idx) {
      activeOptionIdx = idx;
      const opt = options[idx];
      const prod = self.#productCache.get(opt.product_handle);

      titleLink.textContent = opt.product_title;
      const productUrl = prod?.url || opt.product_url || `/products/${opt.product_handle || ''}`;
      titleLink.href = productUrl;
      mediaWrap.href = productUrl;
      // Details verlinkt auf die Bundle-Landingpage (Look-Übersicht),
      // damit der Kunde den ganzen Look kontextualisiert sieht.
      // Convention: /pages/<bundle.handle> — wenn der Marketer den Page-Handle anders gewählt hat, fällt's auf die Produktseite zurück.
      detailLink.href = bundle.handle ? `/pages/${bundle.handle}` : productUrl;

      const qty = Math.max(1, parseInt(opt?.quantity ?? 1, 10) || 1);
      if (qty > 1) {
        qtyBadge.textContent = `× ${qty}`;
        qtyBadge.hidden = false;
      } else {
        qtyBadge.hidden = true;
      }

      // Subline aus Product-Metafield (theme custom.subline) — wenn /products/<handle>.js
      // das Feld liefert. Fallback: nichts anzeigen.
      const subline =
        prod?.metafields?.custom?.subline ||
        opt.product_subline ||
        '';
      if (subline) {
        sublineEl.textContent = subline;
        sublineEl.hidden = false;
      } else {
        sublineEl.hidden = true;
      }

      if (prod && Array.isArray(prod.variants) && prod.variants.length > 0) {
        const firstAvail = prod.variants.find((v) => v.available) || prod.variants[0];
        activeVariantId = firstAvail.id;
        setMedia(prod, firstAvail, opt);
        setPrice(firstAvail, opt);
        renderVariantPicker(prod, opt);
      } else {
        activeVariantId = opt.default_variant_id;
        setMedia(null, null, opt);
        // Ohne product.js Daten können wir nur Override-Preise rendern (kein variant.price bekannt)
        if (opt.price_override_amount != null) {
          priceNowEl.textContent = fmt(Math.round(opt.price_override_amount * 100));
          priceWasEl.hidden = true;
        } else {
          priceNowEl.textContent = '';
          priceWasEl.hidden = true;
        }
        variantOptionsEl.innerHTML = '';
      }
    }

    applyOption(0);
    return card;
  }

  async #upgradeAnchorLines(bundle) {
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      const items = cart?.items || [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const props = item.properties || {};
        if (props._suggested_bundle_handle !== bundle.handle) continue;
        // Schon aufgewertet?
        if (props._bundle_handle === bundle.handle) continue;
        if (!item.product_id) continue;

        // Welcher Slot gehört zu dieser Produkt-ID?
        const productGid = `gid://shopify/Product/${item.product_id}`;
        const matchedSlot = (bundle.slots || []).find((s) => {
          if (s.product_id === productGid) return true;
          return (s.options || []).some((o) => o.product_id === productGid);
        });
        if (!matchedSlot) continue;

        const newProps = { ...props };
        newProps._bundle_handle = bundle.handle;
        if (matchedSlot.slot_id) newProps._bundle_slot_id = matchedSlot.slot_id;
        newProps._bundle_source = newProps._bundle_source || 'lora-anchor-upgrade';
        // _suggested_bundle_handle kann bleiben — Function ignoriert es, wenn _bundle_handle gesetzt ist.

        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: i + 1, properties: newProps }),
          });
        } catch (e) {
          console.warn('[lora-upsell] anchor upgrade failed for line', i + 1, e);
        }
      }
    } catch (e) {
      console.warn('[lora-upsell] upgrade scan failed', e);
    }
  }

  async #addUpsellItem(bundle, slot, option, variantId, button) {
    const vid = variantId || option?.default_variant_id;
    if (!vid) return;
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = '…';
    const props = {
      _bundle_handle: bundle.handle,
      _bundle_source: 'lora-drawer-upsell',
    };
    if (slot.slot_id) props._bundle_slot_id = slot.slot_id;

    // Vor dem Add: bestehende Anchor-Lines aufwerten, damit das Bundle komplett ist
    await this.#upgradeAnchorLines(bundle);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((c) => {
      if (c instanceof HTMLElement && c.dataset.sectionId) sectionIds.push(c.dataset.sectionId);
    });

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: vid, quantity: option.quantity || 1, properties: props }],
          sections: sectionIds.join(','),
        }),
      });
      const result = await res.json();
      if (result.status) {
        console.warn('[lora-upsell] add failed', result.message);
        button.textContent = originalText;
        button.disabled = false;
        return;
      }
      document.dispatchEvent(
        new CartAddEvent(result, this.id, {
          source: 'lora-drawer-upsell',
          itemCount: option.quantity || 1,
          sections: result.sections,
        }),
      );
    } catch (e) {
      console.error('[lora-upsell] add error', e);
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
